import React from 'react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
}

type ButtonVariant = "black" | "white" | "primary" | "create" | "delete" | "outline" | "ghost";

interface StyledDropdownProps {
  options: DropdownOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  buttonClassName?: string;
  buttonVariant?: ButtonVariant;
}

const StyledDropdown: React.FC<StyledDropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  placeholder = 'Select option',
  buttonVariant = 'ghost',
  className = '',
  buttonClassName = '',
  disabled = false,
  isLoading = false,
}) => {
  const getButtonTitle = (): string => {
    if (selectedValue) {
      const selectedOption = options.find(option => option.value === selectedValue);
      return selectedOption?.label || placeholder;
    }
    return placeholder;
  };

  if (isLoading) {
    return (
      <div className={className}>
        <Button
          size="med"
          variant="ghost"
          className="flex items-center gap-2 focus-visible:ring-0 opacity-50"
          disabled
        >
          Loading...
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="med"
            variant={buttonVariant}
            className={`flex items-center gap-2 focus-visible:ring-0 ${buttonClassName}`}
            disabled={disabled}
          >
            {getButtonTitle()}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-64"
          align="end"
        >
          {options.length === 0 ? (
            <DropdownMenuItem
              disabled
              className="text-gray-500"
            >
              No options available
            </DropdownMenuItem>
          ) : (
            options.map((option) => (
              <DropdownMenuItem
                key={option.value}
                className="cursor-pointer"
                onClick={() => onSelect(option.value)}
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {option.label}
                  </div>
                  {option.subtitle && (
                    <div className="text-xs text-gray-500">
                      {option.subtitle}
                    </div>
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default StyledDropdown;